import { FieldState, FormState } from 'formstate';
import { toJS, isObservableMap, runInAction, transaction, computed, comparer } from 'mobx';
export class CheckboxFieldState extends FieldState {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "onChangeHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (_0, checked) => {
                this.onChange(checked);
            }
        });
        // eslint-disable-next-line @typescript-eslint/naming-convention
        Object.defineProperty(this, "DEPRECATED_onChangeHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (_0, data) => {
                this.onChange(!!data.checked);
            }
        });
    }
}
export class InputFieldState extends FieldState {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "onChangeHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (_0, data) => {
                this.onChange(data.value);
            }
        });
        Object.defineProperty(this, "onChangeNativeHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                this.onChange(event.currentTarget.value);
            }
        });
    }
}
export class TextAreaFieldState extends FieldState {
    constructor() {
        super(...arguments);
        /**
         * react-semantic type for onChange event of TextArea component seems to have a bug with data object type
         * so declaring data as any
         */
        Object.defineProperty(this, "onChangeHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (_0, data) => {
                this.onChange(data.value);
            }
        });
    }
}
export class DropdownFieldState extends FieldState {
    constructor() {
        super(...arguments);
        /**
         * react-semantic type for onChange event of Dropdown component seems to have a bug with data object type
         * so declaring data as any
         */
        Object.defineProperty(this, "onChangeHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (_0, data) => {
                this.onChange(data.value);
            }
        });
    }
}
export class DropdownSearchFieldState extends FieldState {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "onChangeHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (_0, data) => {
                this.onChange(data.searchQuery);
            }
        });
    }
}
export class DatetimeFieldState extends FieldState {
    constructor() {
        super(...arguments);
        Object.defineProperty(this, "onChangeHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: (event) => {
                this.onChange(event.target.value);
            }
        });
    }
}
export function enumToOptions(enumObject, nameProvider) {
    return getEnumKeys(enumObject).map(k => ({
        key: k,
        text: nameProvider ? nameProvider(enumObject[k]) : k,
        value: enumObject[k],
    }));
}
export function getEnumKeys(enumObject) {
    let keys = Object.keys(enumObject);
    if (keys.some(k => typeof enumObject[k] === 'number')) {
        keys = keys.filter(k => typeof enumObject[k] === 'number');
    }
    return keys;
}
export function getEnumValues(enumObject) {
    return getEnumKeys(enumObject).map(k => enumObject[k]);
}
export function traverseFormState(recursive, formState, onFormVisit, onFieldVisit) {
    const visitChild = (key, child) => {
        if (child instanceof FormState) {
            if (recursive) {
                traverseFormState(recursive, child, onFormVisit, onFieldVisit);
            }
            if (onFormVisit) {
                onFormVisit(key, child);
            }
        }
        else if (onFieldVisit) {
            onFieldVisit(key, child);
        }
    };
    if (Array.isArray(formState.$)) {
        formState.$.forEach((child, index) => visitChild(index.toString(), child));
    }
    else {
        (isObservableMap(formState.$)
            ? Array.from(formState.$)
            : Object.entries(formState.$)).forEach(([key, child]) => visitChild(key, child));
    }
}
// eslint-disable-next-line @typescript-eslint/naming-convention
export function BAD_formStateToJS(formState) {
    const formValues = {};
    traverseFormState(false, formState, (key, form) => (formValues[key] = BAD_formStateToJS(form)), (key, field) => (formValues[key] = toJS(field.value)));
    return formValues;
}
export function formStateToJS(formState) {
    const formValues = Array.isArray(formState.$) ? [] : {};
    traverseFormState(false, formState, (key, form) => (formValues[key] = formStateToJS(form)), (key, field) => (formValues[key] = toJS(field.$)));
    return formValues;
}
export function setFormStateValues(formState, data, triggerValidation = false) {
    if (Array.isArray(formState.$) && data.length !== formState.$.length) {
        throw new Error('Number of elements in data object node is different from number of element in form.' +
            ' All array nodes should match in size before values can be applied to form.');
    }
    transaction(() => {
        traverseFormState(false, formState, (key, form) => {
            if (data instanceof Map || isObservableMap(data)) {
                if (data.has(key)) {
                    setFormStateValues(form, data.get(key));
                }
            }
            else {
                if (Object.prototype.hasOwnProperty.call(data, key)) {
                    setFormStateValues(form, data[key]);
                }
            }
        }, (key, field) => {
            if (data instanceof Map || isObservableMap(data)) {
                if (data.has(key)) {
                    runInAction(() => {
                        field.value = data.get(key);
                        field.$ = data.get(key);
                    });
                }
            }
            else {
                if (Object.prototype.hasOwnProperty.call(data, key)) {
                    runInAction(() => {
                        field.value = data[key];
                        field.$ = data[key];
                    });
                }
            }
        });
        if (triggerValidation) {
            const setValidatedSubFields = (form) => {
                form.validatedSubFields = form.getValues();
            };
            traverseFormState(true, formState, (_0, form) => {
                setValidatedSubFields(form);
            });
            setValidatedSubFields(formState);
            formState.validate();
        }
    });
    return formState;
}
export function commitFormState(formState) {
    traverseFormState(true, formState, undefined, (_0, field) => {
        field.dirty = false;
        field._initValue = field.value;
    });
}
export function camelCaseToTitleCase(value) {
    const regexp = /[A-Z](?=[A-Z][a-z])|[^A-Z](?=[A-Z])|[A-Za-z](?=[^A-Za-z])/g;
    return value.replace(regexp, '$& ');
}
export function isFormStateChanged(formState) {
    return computed(() => {
        let isChanged = false;
        traverseFormState(true, formState, undefined, (_0, field) => {
            const isValueChanged = () => {
                if (field instanceof InputFieldState) {
                    if (field._initValue === undefined && field.value === '') {
                        return false;
                    }
                }
                return !comparer.structural(field._initValue, field.value);
            };
            isChanged = isChanged || (field.dirty === true && isValueChanged());
        });
        return isChanged;
    });
}
//# sourceMappingURL=form-helpers.js.map